import { useState, useEffect, useMemo } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import { Card, Btn, Select, SectionHeader, Toast, Empty, Badge } from './ui';
type Client = Schema['Client']['type'];

const CDN_BASE = (import.meta.env.VITE_CDN_BASE as string) ?? 'https://intentflo.com';

export default function PixelInstaller() {
  const db = useMemo(() => generateClient<Schema>(), []);
  const [clients,  setClients]  = useState<Client[]>([]);
  const [selected, setSelected] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [copied,   setCopied]   = useState<string | null>(null);
  const [toast,    setToast]    = useState<{ msg: string; type: 'success'|'error' } | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await db.models.Client.list();
      setClients((res.data ?? []).filter((c: any) => c.active));
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  function showToast(msg: string, type: 'success'|'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function markInstalled() {
    if (!selected) return;
    setSaving(true);
    try {
      await db.models.Client.update({ id: selected, pixelInstalled: true });
      showToast('Marked as installed');
      await load();
    } catch (e: any) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  const client  = clients.find(c => c.id === selected);
  const snippet = selected
    ? `<!-- Audience Platform Tag -->\n<script src="${CDN_BASE}/tag.js?clientId=${selected}" async></script>`
    : '';

  const installedCount = clients.filter(c => c.pixelInstalled).length;
  const pendingCount   = clients.filter(c => !c.pixelInstalled).length;

  const platforms = [
    {
      label: 'Next.js / React',
      key: 'nextjs',
      code: selected ? `// app/layout.tsx\nimport Script from 'next/script';\n\n<Script\n  src="${CDN_BASE}/tag.js?clientId=${selected}"\n  strategy="afterInteractive"\n/>` : '',
    },
    {
      label: 'WordPress (functions.php)',
      key: 'wp',
      code: selected ? `add_action('wp_head', function() {\n  echo '<script src="${CDN_BASE}/tag.js?clientId=${selected}" async></script>';\n});` : '',
    },
    {
      label: 'Google Tag Manager',
      key: 'gtm',
      code: selected ? `<!-- Custom HTML Tag -->\n<script src="${CDN_BASE}/tag.js?clientId=${selected}" async></script>` : '',
    },
  ];

  return (
    <div>
      <SectionHeader
        title="Pixel Installer"
        sub="Generate the intentFlo tag snippet for each client — fires your first-party tracking AND their Meta Pixel automatically"
      />

      {/* How it works */}
      <Card style={{ marginBottom: 20, padding: '16px 20px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e8edf5', marginBottom: 12 }}>How the intentFlo Tag works</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          {[
            { icon: '🔷', title: 'Your own first-party pixel', desc: 'Collects page views, form submissions, scroll depth, time on page, and custom events — all stored in intentFlo.' },
            { icon: '📘', title: 'Meta Pixel auto-fires', desc: 'Once consent is granted, the tag dynamically loads the client\'s Meta Pixel (fbevents.js) using the Pixel ID from their account.' },
            { icon: '🔒', title: 'GDPR consent built in', desc: 'Shows a cookie banner on first visit. PII (email, phone, name) is only captured and sent after the visitor accepts.' },
            { icon: '⚡', title: 'What we capture', desc: 'Page URL · Referrer · Session ID · Form email/phone/name · Scroll depth milestones · Time on page · Custom events' },
          ].map(item => (
            <div key={item.title} style={{ background: '#080c14', border: '1px solid #111927', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 18, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#cbd5e1', marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 11, color: '#4a5d7a', lineHeight: 1.6 }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Status chips */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        {[
          { label: `${installedCount} Installed`, bg: '#052e16', border: '#065f46', color: '#6ee7b7' },
          { label: `${pendingCount} Pending`,     bg: '#1c1200', border: '#92400e', color: '#fcd34d' },
          { label: `${clients.length} Total`,     bg: '#0c1a3d', border: '#1d4ed8', color: '#93c5fd' },
        ].map(s => (
          <div key={s.label} style={{
            background: s.bg, border: `1px solid ${s.border}`,
            borderRadius: 8, padding: '8px 14px',
            fontSize: 13, fontWeight: 600, color: s.color,
          }}>
            {s.label}
          </div>
        ))}
      </div>

      {/* Client selector */}
      <Card style={{ marginBottom: 20 }}>
        <Select
          label="Select client"
          value={selected}
          onChange={e => setSelected(e.target.value)}
          options={[
            { value: '', label: loading ? 'Loading…' : '— Choose a client —' },
            ...clients.map(c => ({
              value: c.id,
              label: `${c.clientName}${c.pixelInstalled ? ' ✓' : ''}`,
            })),
          ]}
          style={{ marginBottom: 10 }}
        />
        {client && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Badge label={client.pixelInstalled ? 'installed' : 'pending'} />
            {client.metaPixelId
              ? <span style={{ fontSize: 11, color: '#475569' }}>Pixel ID: <span style={{ fontFamily: 'DM Mono, monospace', color: '#64748b' }}>{client.metaPixelId}</span></span>
              : <span style={{ fontSize: 11, color: '#f59e0b' }}>⚠ No Meta Pixel ID — add in Clients tab</span>
            }
          </div>
        )}
      </Card>

      {!selected ? (
        <Empty icon="◉" title="Select a client above" description="Choose a client to generate their tracking snippet" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Main snippet */}
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>intentFlo Tag + Meta Pixel</div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>Paste once into {'<head>'} — fires first-party tracking AND Meta Pixel automatically</div>
              </div>
              <Btn size="sm" variant={copied === 'html' ? 'success' : 'secondary'} onClick={() => copy(snippet, 'html')}>
                {copied === 'html' ? '✓ Copied' : 'Copy'}
              </Btn>
            </div>
            <pre style={{
              background: '#080c14', border: '1px solid #111927', borderRadius: 8,
              padding: 14, margin: 0, fontSize: 12, color: '#93c5fd',
              fontFamily: 'DM Mono, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6,
            }}>
              {snippet}
            </pre>
          </Card>

          {/* Platform snippets */}
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 14 }}>
              Platform-Specific Instructions
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {platforms.map(p => (
                <div key={p.key} style={{ background: '#080c14', border: '1px solid #111927', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', background: '#0a1120', borderBottom: '1px solid #111927' }}>
                    <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>{p.label}</span>
                    <Btn size="sm" variant={copied === p.key ? 'success' : 'ghost'} onClick={() => copy(p.code, p.key)}>
                      {copied === p.key ? '✓' : 'Copy'}
                    </Btn>
                  </div>
                  <pre style={{ margin: 0, padding: 12, fontSize: 11, color: '#94a3b8', fontFamily: 'DM Mono, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.6 }}>
                    {p.code}
                  </pre>
                </div>
              ))}
            </div>
          </Card>

          {/* Manual tracking API */}
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 8 }}>Manual Event API</div>
            <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>
              Client devs can fire custom events after the tag loads:
            </div>
            <pre style={{ background: '#080c14', border: '1px solid #111927', borderRadius: 8, padding: 12, margin: 0, fontSize: 12, color: '#93c5fd', fontFamily: 'DM Mono, monospace', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
{`// Track a purchase
AudienceTag.track('Purchase', { value: 299, currency: 'USD' });

// Identify a logged-in user (only fires if consent granted)
AudienceTag.identify('user@email.com', 'Jane Smith', '+15551234567');

// Check consent state: 'granted' | 'denied' | null
AudienceTag.getConsent();

// Grant consent programmatically (e.g. your own cookie UI)
AudienceTag.grantConsent();`}
            </pre>
          </Card>

          {/* Checklist + mark installed */}
          <Card style={{ background: '#080c14' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 12 }}>Installation Checklist</div>
            {[
              `Snippet added to <head> on every page`,
              `Cookie consent banner appears on first visit`,
              `Meta Pixel ID ${client?.metaPixelId ? '✓ configured' : '⚠ not configured — add in Clients tab'}`,
              `Test: open browser console, look for [AudienceTag] logs`,
              `Test: submit a form → verify contact appears in Contacts tab`,
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '7px 0', borderBottom: i < 4 ? '1px solid #111927' : 'none' }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#1e2d42', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#64748b', flexShrink: 0, marginTop: 1 }}>
                  {i + 1}
                </div>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{item}</span>
              </div>
            ))}
            <div style={{ marginTop: 16 }}>
              {!client?.pixelInstalled ? (
                <Btn onClick={markInstalled} loading={saving}>✓ Mark as Installed</Btn>
              ) : (
                <span style={{ fontSize: 13, color: '#6ee7b7' }}>✓ Pixel confirmed installed</span>
              )}
            </div>
          </Card>
        </div>
      )}

      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
